import { Button } from "@ondit/ui";
import { useQuery } from "@tanstack/react-query";
import { BadgeCheck } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { ApiError, api, type ClaimResult } from "~/core/api/index";
import { useT } from "~/core/i18n";
import { paid } from "~/core/query";
import { PageHead } from "~/core/ui/page";
import { Async } from "~/core/ui/states";

interface Props {
	slug: string;
	orgId: string;
	onVerified: (entityId: string) => void;
}

/**
 * Proving a listing is yours.
 *
 * Both methods are shown at once rather than behind a chooser. Which one a
 * person can do is decided by facts we cannot see — whether they have DNS
 * access, whether the homepage is theirs to edit — and asking them to pick
 * before they know what each involves adds a step that answers nothing.
 *
 * The strings to paste come from the server. Assembling `ondit-verification=…`
 * here would mean two places have to agree on a format, and the one the
 * customer copies would be the one that is not checked.
 */
export function ClaimPage({ slug, orgId, onVerified }: Props) {
	const t = useT();
	// Opening a challenge writes, so it is asked once per visit: never retried
	// on a 500, never refetched behind the person's back, and not kept for the
	// next visit. Outside the account's scope, so no write elsewhere reopens it.
	const challenge = useQuery({
		queryKey: ["claim", slug, orgId],
		queryFn: () => api.openClaim(slug, orgId),
		...paid,
	});
	const [result, setResult] = useState<ClaimResult | null>(null);
	const [checking, setChecking] = useState(false);
	const [failure, setFailure] = useState<string | null>(null);
	async function copy(value: string) {
		try {
			await navigator.clipboard.writeText(value);
			toast.success(t("common.copied"));
		} catch {
			// Refused clipboard access is not an error worth showing: the value
			// is in a selectable field either way.
		}
	}

	async function check(claimId: string, entityId: string) {
		setChecking(true);
		setFailure(null);
		try {
			const outcome = await api.verifyClaim(claimId);
			setResult(outcome);
			if (outcome.status === "verified") onVerified(entityId);
		} catch (error) {
			setFailure(error instanceof ApiError ? error.messageKey : "errors.internal");
		} finally {
			setChecking(false);
		}
	}

	return (
		<Async state={challenge}>
			{(c) => (
				<div className="stack claim-workbench">
					<PageHead icon={<BadgeCheck />} title={t("claim.title", { name: c.entity_name })} />
					<p className="muted">{t("claim.intro", { domain: c.domain })}</p>

					{c.status === "verified" || result?.status === "verified" ? (
						<div className="callout callout-ok">
							<strong>{t("claim.verified")}</strong>
							<p>{t("claim.verified.body", { name: c.entity_name })}</p>
						</div>
					) : (
						<>
							<section className="claim-method">
								<h2>{t("claim.dns.title")}</h2>
								<p className="muted">{t("claim.dns.body")}</p>
								<dl className="kv">
									<dt>{t("claim.dns.type")}</dt>
									<dd>TXT</dd>
									<dt>{t("claim.dns.host")}</dt>
									<dd>
										<code>{c.dns_record_name}</code>
									</dd>
									<dt>{t("claim.dns.value")}</dt>
									<dd className="row">
										<code className="grow">{c.dns_record_value}</code>
										<Button type="button" variant="default" onClick={() => copy(c.dns_record_value)}>
											{t("common.copy")}
										</Button>
									</dd>
								</dl>
							</section>

							<section className="claim-method">
								<h2>{t("claim.meta.title")}</h2>
								<p className="muted">{t("claim.meta.body", { domain: c.domain })}</p>
								<div className="row">
									<code className="grow">{c.meta_tag}</code>
									<Button type="button" variant="default" onClick={() => copy(c.meta_tag)}>
										{t("common.copy")}
									</Button>
								</div>
							</section>

							<div className="row">
								<Button type="button" disabled={checking} onClick={() => check(c.id, c.entity_id)}>
									{checking ? t("claim.checking") : t("claim.check")}
								</Button>
								{failure && <span className="muted">{t(failure)}</span>}
								{!failure && result?.reason && <span className="muted">{t(`claim.reason.${result.reason}`)}</span>}
							</div>
							<p className="muted small">{t("claim.propagation")}</p>
						</>
					)}
				</div>
			)}
		</Async>
	);
}
